// @refresh reset
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { FullScreenTakeover } from '../components/FullScreenTakeover';
import { DataExplorer } from '../components/DrilldownChart';
import type { Analysis } from '../services/analysis.service';
import { Button } from '../components/ui/button';
import { X, Save } from 'lucide-react';
import { saveChart } from '../services/chartSave.service';
import { useQueryClient } from '@tanstack/react-query';

interface ExplorerTab {
  id: string;
  label: string;
  analysis: Analysis;
  originalAnalysisId: string;
  dateRange?: string;
  customDateStart?: string;
  customDateEnd?: string;
  autoApply?: boolean;
  isDirty: boolean;
}

interface DataExplorerContextType {
  openDataExplorer: (params: {
    analysis: Analysis;
    originalAnalysisId: string;
    dateRange?: string;
    customDateStart?: string;
    customDateEnd?: string;
    onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
    onClose?: () => void;
  }) => void;
  closeDataExplorer: () => void;
}

const DataExplorerContext = createContext<DataExplorerContextType | undefined>(undefined);

interface DataExplorerProviderProps {
  children: ReactNode;
}

// @ts-ignore - Vite provides import.meta.env at runtime

let tabCounter = 0;

function deriveTabLabel(analysis: Analysis, isFirst: boolean): string {
  if (isFirst) {
    return analysis.name || 'Data Explorer';
  }
  const name = analysis.name || 'Drilldown';
  const parts = name.split(' - ');
  return parts.length > 1 ? parts[parts.length - 1] : name;
}

export function DataExplorerProvider({ children }: DataExplorerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tabs, setTabs] = useState<ExplorerTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>('');
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null);
  const [onDateRangeChangeCallback, setOnDateRangeChangeCallback] = useState<
    ((dateRange: string, customStart?: string, customEnd?: string) => void) | null
  >(null);
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const editRef = useRef<HTMLSpanElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const openDataExplorer = useCallback((params: {
    analysis: Analysis;
    originalAnalysisId: string;
    dateRange?: string;
    customDateStart?: string;
    customDateEnd?: string;
    onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
    onClose?: () => void;
  }) => {
    const id = `tab-${++tabCounter}`;
    const isEphemeral = !!params.analysis.ephemeral;
    const analysis = isEphemeral
      ? { ...params.analysis, ephemeral: false }
      : params.analysis;
    const tab: ExplorerTab = {
      id,
      label: deriveTabLabel(params.analysis, true),
      analysis,
      originalAnalysisId: params.originalAnalysisId,
      dateRange: params.dateRange,
      customDateStart: params.customDateStart,
      customDateEnd: params.customDateEnd,
      autoApply: !params.originalAnalysisId,
      isDirty: !params.originalAnalysisId || isEphemeral,
    };
    setTabs([tab]);
    setActiveTabId(id);
    if (params.onClose) {
      setOnCloseCallback(() => params.onClose!);
    } else {
      setOnCloseCallback(null);
    }
    if (params.onDateRangeChange) {
      setOnDateRangeChangeCallback(() => params.onDateRangeChange!);
    } else {
      setOnDateRangeChangeCallback(null);
    }
    setIsOpen(true);
  }, []);

  const closeDataExplorer = useCallback(() => {
    setIsOpen(false);
    if (onCloseCallback) {
      onCloseCallback();
    }
    setTimeout(() => {
      setTabs([]);
      setActiveTabId('');
      setOnCloseCallback(null);
      setOnDateRangeChangeCallback(null);
    }, 300);
  }, [onCloseCallback]);

  const handleDrillDown = useCallback((
    analysis: Analysis,
    dateRange?: string,
    customDateStart?: string,
    customDateEnd?: string,
  ) => {
    const id = `tab-${++tabCounter}`;
    const tab: ExplorerTab = {
      id,
      label: deriveTabLabel(analysis, false),
      analysis,
      originalAnalysisId: '',
      dateRange,
      customDateStart,
      customDateEnd,
      autoApply: true,
      isDirty: true,
    };
    setTabs(prev => [...prev, tab]);
    setActiveTabId(id);
  }, [tabs]);

  const closeTab = useCallback((tabId: string) => {
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === tabId);
      if (idx <= 0) return prev; // don't close the first tab
      const next = prev.filter(t => t.id !== tabId);
      // If we're closing the active tab, activate the previous one
      if (activeTabId === tabId) {
        const newActive = next[Math.min(idx - 1, next.length - 1)];
        if (newActive) setActiveTabId(newActive.id);
      }
      return next;
    });
  }, [activeTabId]);

  const startEditingTab = useCallback((tabId: string) => {
    setEditingTabId(tabId);
    setTimeout(() => {
      const el = editRef.current;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }, 0);
  }, []);

  const commitTabRename = useCallback((tabId: string) => {
    const text = editRef.current?.textContent?.trim();
    if (text) {
      setTabs(prev => prev.map(t =>
        t.id === tabId ? { ...t, label: text, isDirty: t.isDirty || text !== t.label } : t
      ));
    }
    setEditingTabId(null);
  }, []);

  const handleAnalysisChange = useCallback((tabId: string, updatedAnalysis: Analysis) => {
    setTabs(prev => prev.map(t =>
      t.id === tabId ? { ...t, analysis: updatedAnalysis, isDirty: true } : t
    ));
  }, []);

  const handleSave = useCallback(async () => {
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (!activeTab) return;
    setIsSaving(true);
    try {
      await saveChart({
        analysis: activeTab.analysis,
        analysisId: activeTab.originalAnalysisId || undefined,
        name: activeTab.label,
        queryClient,
      });
      setTabs(prev => prev.map(t =>
        t.id === activeTabId ? { ...t, isDirty: false } : t
      ));
    } finally {
      setIsSaving(false);
    }
  }, [tabs, activeTabId, queryClient]);

  return (
    <DataExplorerContext.Provider value={{ openDataExplorer, closeDataExplorer }}>
      {children}
      {tabs.length > 0 && (
        <FullScreenTakeover
          show={isOpen}
          onHide={closeDataExplorer}
          noCloseButton={true}
        >
          {/* Tab bar */}
          <div className="px-4 py-2 border-b border-border flex-shrink-0 flex items-center gap-1 overflow-x-auto">
            {tabs.map((tab, idx) => (
              <button
                key={tab.id}
                onClick={() => setActiveTabId(tab.id)}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
                  whitespace-nowrap transition-colors flex-shrink-0
                  ${tab.id === activeTabId
                    ? 'bg-background text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
                title={tab.label}
              >
                {tab.id === activeTabId ? (
                  <span
                    ref={editingTabId === tab.id ? editRef : undefined}
                    contentEditable={editingTabId === tab.id}
                    suppressContentEditableWarning
                    className={editingTabId === tab.id
                      ? 'outline-none cursor-text'
                      : 'hover:underline cursor-text'
                    }
                    onClick={(e) => {
                      if (editingTabId !== tab.id) {
                        e.stopPropagation();
                        startEditingTab(tab.id);
                      }
                    }}
                    onBlur={() => { if (editingTabId === tab.id) commitTabRename(tab.id); }}
                    onKeyDown={(e) => {
                      if (editingTabId !== tab.id) return;
                      if (e.key === 'Enter') { e.preventDefault(); commitTabRename(tab.id); }
                      if (e.key === 'Escape') setEditingTabId(null);
                    }}
                  >
                    {tab.label}
                  </span>
                ) : (
                  <span>{tab.label}</span>
                )}
                {idx > 0 && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                    className="ml-0.5 rounded-sm hover:bg-muted-foreground/20 p-0.5 -mr-1"
                  >
                    <X className="h-3 w-3" />
                  </span>
                )}
              </button>
            ))}
            <div className="flex-1" />
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={isSaving || !tabs.find(t => t.id === activeTabId)?.isDirty}
              className="flex-shrink-0 mr-1"
              title="Save"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
<Button
              variant="ghost"
              size="icon"
              onClick={closeDataExplorer}
              className="h-6 w-6 flex-shrink-0"
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Tab content — render all, hide inactive to preserve state */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {tabs.map(tab => (
              <div
                key={tab.id}
                className="h-full w-full"
                style={{ display: tab.id === activeTabId ? undefined : 'none' }}
              >
                <DataExplorer
                  analysis={tab.analysis}
                  originalAnalysisId={tab.originalAnalysisId}
                  dateRange={tab.dateRange}
                  customDateStart={tab.customDateStart}
                  customDateEnd={tab.customDateEnd}
                  onDateRangeChange={onDateRangeChangeCallback || undefined}
                  onAnalysisChange={(a) => handleAnalysisChange(tab.id, a)}
                  onDrillDown={handleDrillDown}
                  onClose={closeDataExplorer}
                  autoApply={tab.autoApply}
                />
              </div>
            ))}
          </div>
        </FullScreenTakeover>
      )}
    </DataExplorerContext.Provider>
  );
}

// Access Vite env vars in a way that TypeScript accepts
// Vite replaces import.meta.env at build time, so we access it with a type assertion
const getViteEnv = (): { DEV?: boolean; MODE?: string } => {
  // @ts-ignore - Vite provides import.meta.env at runtime, TypeScript doesn't recognize it with bundler module resolution
  return import.meta.env || {};
};

export function useDataExplorer() {
  const context = useContext(DataExplorerContext);
  const [retryCount, setRetryCount] = useState(0);

  // During hot reload, the context might be temporarily unavailable
  // Force a re-render when it becomes available again
  useEffect(() => {
    if (context === undefined) {
      // Context is undefined, schedule a retry
      const timeoutId = setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 100); // Check every 100ms

      return () => clearTimeout(timeoutId);
    }
  }, [context, retryCount]);

  if (context === undefined) {
    // Check if we're in development mode using Vite's env
    const env = getViteEnv();
    const isDev = env.DEV === true || env.MODE === 'development';

    if (isDev) {
      // Return stub functions that won't break the app
      // The useEffect above will trigger a re-render when context becomes available
      return {
        openDataExplorer: () => {
          console.warn('DataExplorerProvider is not available. openDataExplorer called but ignored.');
        },
        closeDataExplorer: () => {},
      };
    }
    throw new Error('useDataExplorer must be used within a DataExplorerProvider');
  }
  return context;
}

// Safe version that returns no-op functions when provider is not available
export function useDataExplorerSafe() {
  const context = useContext(DataExplorerContext);
  if (context === undefined) {
    // Return no-op functions when provider is not available
    return {
      openDataExplorer: () => {
        console.warn('DataExplorerProvider is not available. openDataExplorer called but ignored.');
      },
      closeDataExplorer: () => {},
    };
  }
  return context;
}
